import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export type QualificationStatus = 'draft' | 'open' | 'closed';

export interface QualificationSession {
    session_id?: number;
    season_id: number;
    circuit_id?: number | null;
    circuit_name?: string | null;
    title?: string | null;
    session_date?: string | null;  // "YYYY-MM-DD"
    status: QualificationStatus;
    note?: string | null;
    created_at?: string | null;
    result_count?: number;
}

export interface QualificationRosterEntry {
    player_id: number;
    name: string;
    result_id: number | null;
    lap_time_ms: number | null;
    note?: string | null;
    updated_at?: string | null;
}

/** One driver's result in a single qualification round. */
export interface SeedingRoundResult {
    session_id: number;
    circuit_id: number | null;
    circuit_name: string;
    title: string | null;
    session_date: string | null;   // "YYYY-MM-DD"
    position: number;              // finishing position within that round
    points: number;                // that position scored through the season's points table
    lap_time_ms: number;
}

/** A scored round of the season's shootout. */
export interface SeedingRound {
    session_id: number;
    circuit_id: number | null;
    circuit_name: string;
    title: string | null;
    session_date: string | null;
    driver_count: number;
}

export interface SeedingEntry {
    rank: number;
    player_id: number;
    name_psn: string | null;
    name_gt7: string | null;
    /**
     * Mean of the driver's per-round scores — the shootout is scored like a
     * mini-season, so this is on the same scale as average_points_v3 and is a
     * decimal (two rounds of 80 and 85 give 82.5).
     */
    initial_rating: number;
    rounds_driven: number;
    /** Fastest raw lap across all rounds. Note it may be set on any circuit. */
    best_lap_ms: number;
    /** Per-round breakdown, oldest round first. */
    results: SeedingRoundResult[];
}

export interface SeedingResponse {
    season_id: number;
    has_race_results: boolean;
    session_count: number;
    /** Which points_v* column scored the shootout, e.g. "points_v4". */
    points_column: string;
    rounds: SeedingRound[];
    /** Drivers with no lap in any round are absent — no lap means no rating. */
    entries: SeedingEntry[];
}

export { parseLapTime, formatLapTime, formatGap } from './lapTime.util';

@Injectable()
export class QualificationService {
    private base       = environment.apiBaseUrl;
    private sessionsUrl = `${this.base}/api/qualificationSessionsCrud.php`;
    private resultsUrl  = `${this.base}/api/qualificationResults.php`;
    private seedingUrl  = `${this.base}/api/qualificationSeeding.php`;

    constructor(private http: HttpClient) {}

    getSessions(season_id: number): Observable<QualificationSession[]> {
        return this.http.get<QualificationSession[]>(`${this.sessionsUrl}?season_id=${season_id}`);
    }

    saveSession(s: QualificationSession): Observable<any> {
        return s.session_id
            ? this.http.put<any>(this.sessionsUrl, s)
            : this.http.post<any>(this.sessionsUrl, s);
    }

    deleteSession(session_id: number): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(this.sessionsUrl, { body: { session_id } });
    }

    /** Admin: approved applicants of the session's season with their lap for this session. */
    getRoster(session_id: number): Observable<QualificationRosterEntry[]> {
        return this.http.get<QualificationRosterEntry[]>(`${this.resultsUrl}?session_id=${session_id}`);
    }

    /** Admin: upsert one driver's lap; lap_time_ms = null clears the stored lap. */
    saveResult(entry: {
        session_id: number;
        player_id: number;
        lap_time_ms: number | null;
        note?: string | null;
    }): Observable<any> {
        return this.http.put<any>(this.resultsUrl, entry);
    }

    /** Public: fastest lap + rank + initial rating per driver for a season. */
    getSeeding(season: number): Observable<SeedingResponse> {
        return this.http.get<SeedingResponse>(`${this.seedingUrl}?season=${season}`);
    }
}
